import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

/**
 * Componente de imagen optimizada con:
 * - Lazy loading con Intersection Observer
 * - Progressive loading (blur placeholder)
 * - Soporte WebP con fallback JPEG
 * - Responsive images (srcset)
 * - Skeleton loader
 */
const OptimizedImage = ({
  photo,
  size = "medium", // thumb | medium | large
  alt = "",
  className = "",
  onClick = () => {},
  priority = false, // Cargar inmediatamente (sin lazy)
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Intersection Observer para lazy loading con mejor configuración para hosting
  useEffect(() => {
    if (priority || !imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: size === "thumb" ? "200px" : "100px", // Más margen para thumbnails
        threshold: 0.01,
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [priority, size]);

  // Determinar URLs según el tamaño solicitado
  const getImageUrls = () => {
    // Si photo.sizes existe (nuevo formato en memoria)
    if (photo.sizes && typeof photo.sizes === "object") {
      const sizes =
        photo.sizes[size] || photo.sizes.medium || photo.sizes.large;
      return {
        webp: sizes?.webp,
        jpeg: sizes?.jpeg,
        width: sizes?.width,
        height: sizes?.height,
      };
    }

    // Si photo.image_sizes existe (de DB como JSON string)
    if (photo.image_sizes) {
      try {
        const parsed =
          typeof photo.image_sizes === "string"
            ? JSON.parse(photo.image_sizes)
            : photo.image_sizes;
        const sizes = parsed[size] || parsed.medium || parsed.large;
        return {
          webp: sizes?.webp,
          jpeg: sizes?.jpeg,
          width: sizes?.width,
          height: sizes?.height,
        };
      } catch (e) {
        console.error("Error parsing image_sizes:", e);
      }
    }

    // Fallback para fotos antiguas: usar URL directa sin optimizaciones
    return {
      jpeg: photo.url,
      webp: null,
      width: null,
      height: null,
    };
  };

  const imageUrls = getImageUrls();
  const shouldLoad = isInView || priority;

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden bg-gray-100 ${className}`}
      onClick={onClick}
    >
      {/* Skeleton loader */}
      {!isLoaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%]">
          <style jsx>{`
            @keyframes shimmer {
              0% {
                background-position: -200% 0;
              }
              100% {
                background-position: 200% 0;
              }
            }
            .animate-pulse {
              animation: shimmer 2s infinite;
            }
          `}</style>
        </div>
      )}

      {/* Imagen real */}
      {shouldLoad && (
        <picture>
          {/* WebP para navegadores modernos */}
          {imageUrls.webp && (
            <source srcSet={imageUrls.webp} type="image/webp" />
          )}

          {/* JPEG como fallback */}
          <motion.img
            src={imageUrls.jpeg}
            alt={alt}
            className={`w-full h-full object-cover transition-opacity duration-500 ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}
            style={{
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              userSelect: "none",
            }}
            onLoad={() => {
              setIsLoaded(true);
              // Prefetch siguiente imagen
              if (
                priority &&
                typeof window !== "undefined" &&
                window.requestIdleCallback
              ) {
                window.requestIdleCallback(() => {
                  const link = document.createElement("link");
                  link.rel = "prefetch";
                  link.as = "image";
                  link.href = imageUrls.jpeg;
                  document.head.appendChild(link);
                });
              }
            }}
            onError={(e) => {
              console.error("Error cargando imagen:", imageUrls.jpeg);
              setError(true);
              setIsLoaded(true);
              // Intentar cargar desde CDN alternativo o servidor directo
              if (!e.target.dataset.retried) {
                e.target.dataset.retried = "true";
                const fallbackUrl = imageUrls.jpeg.replace(
                  "/thumb/",
                  "/medium/"
                );
                if (fallbackUrl !== imageUrls.jpeg) {
                  e.target.src = fallbackUrl;
                }
              }
            }}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            fetchpriority={priority ? "high" : "auto"}
            width={imageUrls.width}
            height={imageUrls.height}
            draggable={false}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{
              opacity: isLoaded ? 1 : 0,
              scale: isLoaded ? 1 : 1.1,
            }}
            transition={{ duration: 0.3 }}
          />
        </picture>
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 text-gray-500 text-sm">
          Error al cargar imagen
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
